/**
 * useGeoLocation Hook
 * 
 * Detects user's country based on IP address using free APIs
 */

import { useState, useEffect } from 'react';

// Free IP geolocation APIs (no key required)
const GEO_APIS = [
  'https://ipapi.co/json/',
  'https://ip-api.com/json/'
];

export function useGeoLocation() {
  const [country, setCountry] = useState(null);
  const [countryName, setCountryName] = useState(null);
  const [currency, setCurrency] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const detectLocation = async () => {
      setLoading(true);
      
      // Try each API until one works
      for (const api of GEO_APIS) {
        try {
          const response = await fetch(api, { 
            signal: AbortSignal.timeout(5000) // 5 second timeout
          });
          
          if (!response.ok) {
            console.warn(`Geo API returned status ${response.status}: ${api}`);
            continue;
          }
          
          const data = await response.json();
          
          // Different APIs have different response formats
          const countryCode = data.country_code || data.countryCode || data.country;
          const countryFullName = data.country_name || data.country;
          const currencyCode = data.currency || data.currency_code || 'USD';
          
          if (countryCode) {
            setCountry(countryCode);
            setCountryName(countryFullName);
            setCurrency(currencyCode);
            setLoading(false);
            return;
          }
        } catch (err) {
          console.warn(`Geo API failed: ${api}`, err.message || err);
          continue;
        }
      }
      
      // If all APIs fail, default to US
      setCountry('US');
      setCountryName('United States');
      setCurrency('USD');
      setError('Could not detect location, using default');
      setLoading(false);
    };

    detectLocation();
  }, []);

  return { country, countryName, currency, loading, error };
}

/**
 * useExchangeRate Hook
 * 
 * Fetches USDC to local currency rate from CoinGecko
 */
export function useExchangeRate(targetCurrency = 'usd') {
  const [rate, setRate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchRate = async () => {
      if (!targetCurrency) {
        setLoading(false);
        return;
      }

      setLoading(true);
      
      try {
        // CoinGecko free API - no key needed
        const response = await fetch(
          `https://api.coingecko.com/api/v3/simple/price?ids=usd-coin&vs_currencies=${targetCurrency.toLowerCase()}`,
          { signal: AbortSignal.timeout(5000) } // 5 second timeout
        );
        
        if (!response.ok) {
          throw new Error(`Rate API returned status ${response.status}`);
        }
        
        const data = await response.json();
        const usdcRate = data['usd-coin']?.[targetCurrency.toLowerCase()];
        
        if (usdcRate) {
          setRate(usdcRate);
        } else {
          // Fallback: assume 1:1 for USD-pegged
          setRate(targetCurrency.toLowerCase() === 'usd' ? 1 : null);
        }
      } catch (err) {
        console.warn('Failed to fetch exchange rate:', err.message || err);
        setError(err.message);
        // Default to 1 for USD
        if (targetCurrency.toLowerCase() === 'usd') {
          setRate(1);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchRate();
  }, [targetCurrency]);

  // Calculate estimated amount
  const getEstimatedAmount = (usdcAmount) => {
    if (!rate || !usdcAmount) return null;
    return (usdcAmount * rate).toFixed(2);
  };

  return { rate, loading, error, getEstimatedAmount };
}

export default useGeoLocation;
